import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { Locale } from 'date-fns/locale';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrderForAssetAvailability } from '../../../api/types';
import { makeAssetDetailsUrl } from '../../../routes';
import CustomButton from '../../../components/CustomButton';
import { CUSTOM_MODAL_Z_INDEX } from '../../common/CustomModal';
import AssetDayOrders, { DayTimelineHead } from './AssetDayOrders';

// The whole day, across every object the table is currently showing: one
// section per object with its bookings and its free windows, so "what is free
// today at 14:00?" is answered in one look instead of object by object.
//
// The sections are the same component the single-asset dialog uses; this screen
// only stacks them under one shared hour ruler and gives them a heading.

export interface DayOverlayObject {
    assetId: string;
    /** As the table labels it — locker number included where there is one. */
    displayName: string;
    /** Location and use case, where the table shows them. */
    subtitle?: string;
    /** The day's orders, already narrowed by the table's active filters. */
    orders: OrderForAssetAvailability[];
}

interface DayOccupancyOverlayProps {
    day: Date;
    locale: Locale;
    objects: DayOverlayObject[];
    onClose: () => void;
}

const DayOccupancyOverlay: React.FC<DayOccupancyOverlayProps> = (
    { day, locale, objects, onClose },
) => {
    const { t } = useTranslation();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const dayLabel = format(day, 'PPP', { locale });

    return createPortal(
        <div
            style={{ zIndex: CUSTOM_MODAL_Z_INDEX }}
            className={
                'fixed inset-0 flex items-center justify-center ' +
                'bg-black bg-opacity-40 p-4'
            }
        >
            {/* Click-outside-to-close as a real button behind the panel, not a
                click handler on the backdrop div: a div that reacts to clicks
                is unreachable by keyboard, and swallowing the bubbled click on
                the panel to protect it is the same defect one level down. As a
                sibling the panel simply paints on top, so nothing bubbles. */}
            <button
                type={'button'}
                onClick={onClose}
                aria-label={t('dashboard.assets_availability.dialog.close')}
                className={'absolute inset-0 w-full h-full cursor-default'}
            />

            {/* Capped at the viewport and laid out as a column so the object
                list scrolls on its own — this has to stay usable at 30+
                objects — while ruler, title and actions stay put. */}
            <div
                role={'dialog'}
                aria-modal
                aria-label={t('dashboard.assets_availability.day_view.title', {
                    date: dayLabel,
                })}
                className={
                    'relative bg-text-contrast rounded-xl shadow-lg w-full ' +
                    'max-w-5xl max-h-[90vh] flex flex-col'
                }
            >
                <div className={'flex items-start justify-between gap-4 px-6 pt-5 pb-3'}>
                    <div>
                        <h2 className={'text-xl font-semibold text-foreground'}>
                            {t('dashboard.assets_availability.day_view.title', {
                                date: dayLabel,
                            })}
                        </h2>
                        <p className={'text-sm text-foreground/60 mt-0.5'}>
                            {t('dashboard.assets_availability.day_view.objects', {
                                count: objects.length,
                            })}
                        </p>
                    </div>
                    <button
                        type={'button'}
                        onClick={onClose}
                        aria-label={t('dashboard.assets_availability.dialog.close')}
                        className={
                            'text-foreground/60 hover:text-foreground transition-colors'
                        }
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className={'px-6 overflow-y-auto flex-1 min-h-0'}>
                    {objects.length === 0 ? (
                        <p className={'text-sm text-foreground/60 py-8 text-center'}>
                            {t('dashboard.assets_availability.empty')}
                        </p>
                    ) : (
                        <table className={'w-full text-sm table-fixed'}>
                            <DayTimelineHead sticky />
                            {objects.map((object) => (
                                <AssetDayOrders
                                    key={object.assetId}
                                    day={day}
                                    orders={object.orders}
                                    heading={
                                        <div className={
                                            'flex flex-wrap items-baseline gap-x-2 ' +
                                            'border-t border-foreground/10 pt-3'
                                        }
                                        >
                                            <Link
                                                to={makeAssetDetailsUrl(object.assetId)}
                                                className={
                                                    'font-semibold text-foreground ' +
                                                    'hover:text-primary hover:underline ' +
                                                    'transition-colors'
                                                }
                                            >
                                                {object.displayName}
                                            </Link>
                                            {object.subtitle && (
                                                <span className={'text-xs text-foreground/60'}>
                                                    {object.subtitle}
                                                </span>
                                            )}
                                        </div>
                                    }
                                />
                            ))}
                        </table>
                    )}
                </div>

                <div className={'flex justify-end px-6 pb-5 pt-3 shrink-0'}>
                    <CustomButton
                        title={t('dashboard.assets_availability.dialog.close')}
                        onClick={onClose}
                    />
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default DayOccupancyOverlay;
